import { styled } from "goober";
import type { ReactNode } from "react";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { Text } from "./Text";

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
`;

const TitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const List = styled("div")`
  margin: 0;
  padding: 4px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-secondary);
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Row = styled("a")`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }

  & .arrow-icon {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
  }

  &:hover .arrow-icon {
    opacity: 0.5;
  }
`;

const BadgeSlot = styled("span")`
  display: flex;
  flex-shrink: 0;
`;

const RowText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

type TidyUpItem = {
  key: string;
  href: string;
  badge: ReactNode;
  name: string;
  subtitle?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemNoun: { singular: string; plural: string };
  inactivityDays: number;
  // Optional line above the list, e.g. a warning that removal also deletes something.
  summary?: ReactNode;
  items: TidyUpItem[];
};

export function TidyUpModal({
  open,
  onClose,
  onConfirm,
  itemNoun,
  inactivityDays,
  summary,
  items,
}: Props) {
  const count = items.length;
  const noun = count === 1 ? itemNoun.singular : itemNoun.plural;
  const description =
    count > 0
      ? `You have ${count} ${noun} with no chats or runs in the last ${inactivityDays} days. Remove ${
          count === 1 ? "it" : "them"
        } to keep things tidy.`
      : `Nothing to tidy — all your ${itemNoun.plural} have been active in the last ${inactivityDays} days. You’re already on top of things.`;
  return (
    <Modal open={open} onClose={onClose}>
      <Content>
        <TitleBlock>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Tidy up
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            {description}
          </Text>
        </TitleBlock>
        {items.length > 0 && (
          <Body>
            {summary}
            <List>
              {items.map((item) => (
                <Row
                  key={item.key}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BadgeSlot>{item.badge}</BadgeSlot>
                  <RowText>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {item.name}
                    </Text>
                    {item.subtitle && (
                      <Text variant="text-small" color="var(--text-secondary)">
                        {item.subtitle}
                      </Text>
                    )}
                  </RowText>
                  <span className="arrow-icon">
                    <Icon name="arrow-up-right" size={12} />
                  </span>
                </Row>
              ))}
            </List>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button size="l" variant="danger" onClick={onConfirm}>
                {items.length === 1 ? "Delete" : "Delete all"}
              </Button>
            </div>
          </Body>
        )}
      </Content>
    </Modal>
  );
}
